/**
 * =============================================================================
 * ADMIN ERROR BOUNDARY
 * =============================================================================
 *
 * Error boundary component for the admin dashboard.
 * Catches and handles React errors gracefully, preventing crashes from
 * affecting the entire admin interface.
 *
 * ARCHITECTURE REFERENCE: Section 10 - Admin Dashboard
 * PHASE 1 TASK: P1-18 - Admin Error Boundary
 *
 * Features:
 * - Catches render errors in child components
 * - Displays user-friendly error message with BigDesign styling
 * - Provides "Try Again" button to reset the error state
 * - Logs errors for debugging
 *
 * =============================================================================
 */

import { Component } from 'react';
import type { ReactNode, ErrorInfo } from 'react';
import { Panel, Box, Button, Text, Flex, FlexItem } from '@bigcommerce/big-design';
import { WarningIcon } from '@bigcommerce/big-design-icons';
import { logger } from '../../lib/logger';

// =============================================================================
// TYPES
// =============================================================================

export interface AdminErrorBoundaryProps {
  /**
   * Child components to be wrapped by the error boundary.
   */
  children: ReactNode;
  /**
   * Optional custom fallback component to render on error.
   * If not provided, a default error UI is shown.
   */
  fallback?: ReactNode;
}

interface AdminErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

// =============================================================================
// COMPONENT
// =============================================================================

/**
 * Error boundary component for the admin dashboard.
 *
 * Wraps child components and catches any React errors during rendering,
 * in lifecycle methods, and in constructors of the whole tree below them.
 *
 * @see https://react.dev/reference/react/Component#catching-rendering-errors-with-an-error-boundary
 *
 * @example
 * ```tsx
 * <AdminErrorBoundary>
 *   <QuestionQueue />
 * </AdminErrorBoundary>
 * ```
 */
export class AdminErrorBoundary extends Component<AdminErrorBoundaryProps, AdminErrorBoundaryState> {
  constructor(props: AdminErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  /**
   * Update state when an error occurs so the next render shows the fallback UI.
   */
  static getDerivedStateFromError(error: Error): Partial<AdminErrorBoundaryState> {
    return { hasError: true, error };
  }

  /**
   * Log error information for debugging.
   */
  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    this.setState({ errorInfo });

    // Log error for debugging
    // In production, this could be sent to an error tracking service
    logger.error('BOUNDARY_CAUGHT_ERROR', error, {
      componentStack: errorInfo.componentStack,
    });
  }

  /**
   * Reset the error state to attempt re-rendering children.
   */
  handleReset = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  /**
   * Reload the page for a complete fresh start.
   */
  handleReload = (): void => {
    window.location.reload();
  };

  render(): ReactNode {
    const { hasError, error } = this.state;
    const { children, fallback } = this.props;

    if (hasError) {
      // If a custom fallback is provided, use it
      if (fallback) {
        return fallback;
      }

      // Default error UI using BigDesign components
      return (
        <Box marginVertical="xxLarge">
          <Panel
            header="Something went wrong"
            action={{
              text: 'Reload Page',
              variant: 'secondary',
              onClick: this.handleReload,
            }}
          >
            <Flex alignItems="flex-start" marginBottom="medium">
              <FlexItem>
                <Box marginRight="small">
                  <WarningIcon color="danger" size="xLarge" />
                </Box>
              </FlexItem>
              <FlexItem flexGrow={1}>
                <Text color="secondary">
                  An unexpected error occurred while loading the admin dashboard.
                  Please try again, or reload the page if the problem persists.
                </Text>
                {error && import.meta.env.DEV && (
                  <Box marginTop="small">
                    <Text color="danger" as="code">
                      {error.message}
                    </Text>
                  </Box>
                )}
              </FlexItem>
            </Flex>
            <Flex justifyContent="flex-start">
              <FlexItem>
                <Button
                  variant="primary"
                  onClick={this.handleReset}
                  data-testid="error-boundary-retry"
                >
                  Try Again
                </Button>
              </FlexItem>
            </Flex>
          </Panel>
        </Box>
      );
    }

    return children;
  }
}
